import { TURNSTILE_ERROR_MESSAGE } from '@/components/Turnstile';

/**
 * What a visitor reads when sign-in, sign-up or a password reset fails.
 *
 * The forms keep storing the raw text they always stored (Supabase's message,
 * the `?error=` that /auth/callback appends, the reason /api/validate-email
 * returns, our own sentences). This module only decides how it reads: a
 * known message becomes a plain sentence (a `login` namespace key) and a
 * reason code for the `Auth Error` event; anything else is shown as it came.
 * The reason never carries the raw text, which can hold an email address or
 * an OAuth code.
 */
export type AuthErrorReason =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'captcha'
  | 'rate_limited'
  | 'network'
  | 'weak_password'
  | 'same_password'
  | 'existing_account'
  | 'gmail_inbox_taken'
  | 'blocked_email'
  | 'email_disposable'
  | 'email_plus'
  | 'email_no_mx'
  | 'email_ai_rejected'
  | 'email_invalid'
  | 'device_limit'
  | 'password_policy'
  | 'password_mismatch'
  | 'missing_email'
  | 'oauth_cancelled'
  | 'oauth_session_lost'
  | 'oauth_no_email'
  | 'oauth_permissions'
  | 'oauth_exchange_failed'
  | 'expired_link'
  | 'session_missing'
  | 'delivery_failed'
  | 'unexpected'
  | 'other';

export type AuthMessage = {
  /** A `login` namespace key (an English sentence), or the raw text. */
  key: string;
  values?: Record<string, string | number>;
  reason: AuthErrorReason;
};

type Rule = {
  test: RegExp | string;
  key: string;
  reason: AuthErrorReason;
  /** Placeholder values taken from the raw text's capture groups. */
  values?: (match: RegExpMatchArray) => Record<string, string | number>;
};

// Sentences our own code and /api/validate-email return. They are already
// readable, so they translate as they are. Copies of server constants
// (`@/utils/email-validation`, `@/lib/signupAiEmailCheck`): importing those
// modules would pull server code into the browser, so
// scripts/test-auth-i18n.ts checks the copies still match.
export const DELETED_USER_REASON =
  'This email has been blocked. Please use a different email address.';
export const GMAIL_INBOX_TAKEN_REASON =
  'This Gmail address already has an account. Gmail ignores dots, so john.smith@gmail.com and johnsmith@gmail.com are the same address. Please sign in instead.';
export const SIGNUP_AI_REJECTION_REASON =
  "We couldn't verify this email address. Please use your personal or work email, or continue with Google.";

export const EXISTING_ACCOUNT_PAGE =
  'An account with this email already exists. Sign in below: use Google if that is how you signed up, or email yourself a sign-in link.';
export const EXISTING_ACCOUNT_MODAL =
  'An account with this email already exists. Sign in below: use Google if that is how you signed up, or reset your password.';

const VERBATIM: Array<[string, AuthErrorReason]> = [
  [TURNSTILE_ERROR_MESSAGE, 'captcha'],
  [DELETED_USER_REASON, 'blocked_email'],
  [GMAIL_INBOX_TAKEN_REASON, 'gmail_inbox_taken'],
  ['Please use a non-disposable email address to sign up.', 'email_disposable'],
  [
    'Email addresses with "+" are not allowed. Please use your primary email address.',
    'email_plus',
  ],
  [
    'This email domain cannot receive mail. Please use a different email address.',
    'email_no_mx',
  ],
  [SIGNUP_AI_REJECTION_REASON, 'email_ai_rejected'],
  ['Failed to validate email. Please try again.', 'unexpected'],
  ['Could not resend the email. Please try again.', 'unexpected'],
  ['Enter your email address first.', 'missing_email'],
  ['New password should be different from the old password.', 'same_password'],
  // Sign-up found a confirmed account (/login offers a sign-in link below,
  // the modal a password reset).
  [EXISTING_ACCOUNT_PAGE, 'existing_account'],
  [EXISTING_ACCOUNT_MODAL, 'existing_account'],
];

const RULES: Rule[] = [
  {
    test: /^invalid login credentials$/i,
    key: "That email and password don't match an account. Check them and try again.",
    reason: 'invalid_credentials',
  },
  {
    test: /email not confirmed/i,
    key: 'Confirm your email address first: open the link we sent you when you signed up.',
    reason: 'email_not_confirmed',
  },
  {
    test: /captcha/i,
    key: TURNSTILE_ERROR_MESSAGE,
    reason: 'captcha',
  },
  {
    test: /only request this after (\d+) seconds?/i,
    key: 'For your security, please wait {{seconds}} seconds before trying again.',
    reason: 'rate_limited',
    values: (match) => ({ seconds: Number(match[1]) }),
  },
  {
    test: /rate limit|too many requests/i,
    key: 'Too many attempts. Please wait a minute and try again.',
    reason: 'rate_limited',
  },
  {
    test: /failed to fetch|networkerror|load failed|network request failed|fetch failed/i,
    key: "We couldn't reach Easy-Peasy.AI. Check your connection and try again.",
    reason: 'network',
  },
  {
    test: /known to be weak|easy to guess|pwned|leaked|data breach/i,
    key: 'Choose a different password: this one is too easy to guess or has appeared in a data breach.',
    reason: 'weak_password',
  },
  {
    test: /user already registered|^an account with this email already exists/i,
    key: 'An account with this email already exists. Please sign in instead.',
    reason: 'existing_account',
  },
  {
    test: /limit of (\d+) accounts on this device/i,
    key: "You've reached the limit of {{limit}} accounts on this device. Please continue with an existing email or contact support for assistance.",
    reason: 'device_limit',
    values: (match) => ({ limit: Number(match[1]) }),
  },
  {
    test: /^password (must|does not)|^this password is too common/i,
    key: "Your password doesn't meet all the requirements yet.",
    reason: 'password_policy',
  },
  {
    test: /^passwords do not match$/i,
    key: "The two passwords don't match.",
    reason: 'password_mismatch',
  },
  {
    test: /^please enter your email address$|^email is required$/i,
    key: 'Enter your email address first.',
    reason: 'missing_email',
  },
  {
    test: /invalid format|^invalid email address$|email address .* is invalid/i,
    key: 'Enter a valid email address.',
    reason: 'email_invalid',
  },
  // What /auth/callback forwards from the OAuth provider or Supabase.
  {
    test: /access_denied|user cancel|user denied|denied access/i,
    key: 'Sign-in was cancelled. Try again, or continue with your email.',
    reason: 'oauth_cancelled',
  },
  {
    test: /code challenge does not match|code verifier should be non-empty|flow state|state has already been used|bad_oauth_state/i,
    key: 'Your sign-in expired before it finished. This can happen when it starts in another tab or browser. Please try again.',
    reason: 'oauth_session_lost',
  },
  {
    test: /error getting user email from external provider/i,
    key: "We didn't receive an email address from that account. Try again and allow access to your email, or sign in another way.",
    reason: 'oauth_no_email',
  },
  {
    test: /permissions error/i,
    key: "We didn't get permission to sign you in. Try again and allow access, or sign in another way.",
    reason: 'oauth_permissions',
  },
  {
    test: /unable to exchange external code/i,
    key: "We couldn't finish signing you in with that account. Please try again.",
    reason: 'oauth_exchange_failed',
  },
  // The password reset page.
  {
    test: /reset link has expired or is invalid|email link is invalid or has expired/i,
    key: 'This reset link has expired or was already used. Request a new one below.',
    reason: 'expired_link',
  },
  {
    test: /session is not ready|auth session missing|no valid reset token/i,
    key: "This reset link isn't active. Open the latest link from your email, or request a new one.",
    reason: 'session_missing',
  },
  {
    test: /^an unexpected error occurred$|^authentication error occurred$/i,
    key: 'Something went wrong. Please try again.',
    reason: 'unexpected',
  },
];

export function describeAuthMessage(raw: string): AuthMessage {
  const text = (raw || '').trim();
  for (const [sentence, reason] of VERBATIM) {
    if (text === sentence) return { key: sentence, reason };
  }
  for (const rule of RULES) {
    if (typeof rule.test === 'string') {
      if (text === rule.test) return { key: rule.key, reason: rule.reason };
      continue;
    }
    const match = text.match(rule.test);
    if (match) {
      return {
        key: rule.key,
        reason: rule.reason,
        values: rule.values?.(match),
      };
    }
  }
  return { key: text, reason: 'other' };
}

/** OAuth problems belong at the top of the form, next to the buttons. */
export const isOAuthReason = (reason: AuthErrorReason) =>
  reason.startsWith('oauth_') || reason === 'blocked_email';

/**
 * Success and info texts the forms store and show through the same path
 * (they fall through to "shown as it came", translated as a key).
 */
export const AUTH_NOTICE_KEYS = [
  'A new confirmation email is on its way.',
  'A fresh password reset link is on its way. Check your inbox.',
  'A fresh confirmation link is on its way. Check your inbox.',
  'Good news: this email is already verified. Just sign in.',
];

/** Every sentence this module can show, for the locale check script. */
export const AUTH_MESSAGE_KEYS: string[] = [
  ...VERBATIM.map(([sentence]) => sentence),
  ...RULES.map((rule) => rule.key),
  ...AUTH_NOTICE_KEYS,
];
